import { useState, useCallback, useRef, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiService } from "../../services/api";
import authService from "../../services/authService";
import OrderPDF from "../../components/order/OrderPDF";
import { printOrder } from "../../utils/printOrder";
import { ORDER_STATUS_TABS, OrderRow } from "../../components/order/OrdersGrid";

const OrderHistoryPage = () => {
  // ?order=<id> opens one order: All tab, searched to its number, expanded (e.g. from the Credits page).
  const [searchParams] = useSearchParams();
  const linkedOrderId = Number(searchParams.get("order")) || null;

  const [activeTab, setActiveTab] = useState(linkedOrderId ? "all" : "pendingDelivery");
  const [expandedOrder, setExpandedOrder] = useState(linkedOrderId);
  const [page, setPage] = useState(1);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printOrderId, setPrintOrderId] = useState(null);
  const [searchTerm, setSearchTerm] = useState(linkedOrderId ? String(linkedOrderId) : "");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(linkedOrderId ? String(linkedOrderId) : "");
  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'desc' });
  const pageSize = 10;
  const printRef = useRef(null);

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== debouncedSearchTerm) {
        setDebouncedSearchTerm(searchTerm);
        setPage(1); // Reset to first page when search changes
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearchTerm]);

  // Get current store info
  const currentStore = authService.getCurrentStore();
  const clientId = currentStore?.id; // store.id is the clientId

  // Build query for fetching orders
  const buildOrdersQuery = useCallback((statusFilter) => {
    let filter = `ClientID eq ${clientId}`;
    if (statusFilter) {
      filter += ` and ${statusFilter}`;
    }

    // Add search filter for order number (server-side search)
    if (debouncedSearchTerm.trim()) {
      const term = debouncedSearchTerm.trim();
      // Search by LegacyInvoice (string) or OrderID (int)
      filter += ` and (contains(cast(OrderID, 'Edm.String'), '${term}') or contains(LegacyInvoice, '${term}'))`;
    }

    // Determine sort order
    let orderBy = "RequestedDeliveryDate desc"; // default
    if (sortConfig.column === 'datePlaced') {
      orderBy = `DateCreated ${sortConfig.direction}`;
    } else if (sortConfig.column === 'deliveryDate') {
      orderBy = `RequestedDeliveryDate ${sortConfig.direction}`;
    }

    return {
      $filter: filter,
      $orderby: orderBy,
      $expand: "Client,OptFulfillmentStatus,OptDeliveryStatus,OptPaymentStatus",
      $top: pageSize,
      $skip: (page - 1) * pageSize,
      $count: true
    };
  }, [clientId, page, pageSize, sortConfig, debouncedSearchTerm]);

  // Fetch orders using OrderTotals view (same as admin portal)
  const {
    data: ordersData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ["storeOrders", clientId, activeTab, page, sortConfig, debouncedSearchTerm],
    queryFn: async () => {
      if (!clientId) return { orders: [], totalCount: 0 };

      const statusConfig = ORDER_STATUS_TABS[activeTab];
      const queryParams = buildOrdersQuery(statusConfig.filter);

      const response = await apiService.get("/storefront-odata/StorefrontOrderHistory", queryParams);

      return {
        orders: response.value || [],
        totalCount: response["@odata.count"] || 0
      };
    },
    enabled: !!clientId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Fetch order details when expanded
  const {
    data: orderDetails,
    isLoading: isLoadingDetails
  } = useQuery({
    queryKey: ["orderDetails", expandedOrder],
    queryFn: async () => {
      if (!expandedOrder) return null;

      const response = await apiService.get(`/storefront-odata/StorefrontOrders(${expandedOrder})`, {
        $expand: "Client($expand=Company($expand=State),State),OrderItems($expand=ProductSKUVariant($expand=ProductSKU($expand=Brand)),ProductBatchLot($expand=ProductBatch)),OptPaymentStatus,OptFulfillmentStatus,OptDeliveryStatus,ClientSalesRepUser"
      });

      return response;
    },
    enabled: !!expandedOrder,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const orders = ordersData?.orders || [];
  const totalCount = ordersData?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Handle column sorting (server-side)
  const handleSort = (column) => {
    setSortConfig(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setPage(1); // Reset to first page when sort changes
  };

  // Handle search input change (server-side search)
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Toggle order expansion
  const toggleOrderExpand = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
    setExpandedOrder(null);
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setSortConfig({ column: null, direction: 'desc' });
  };

  // Handle print order
  const handlePrintOrder = useCallback((orderId) => {
    if (!orderId) return;
    setIsPrinting(true);
    setPrintOrderId(orderId);
  }, []);

  const handlePrintReady = useCallback(() => {
    if (printRef.current) {
      printOrder(printRef, { id: printOrderId });
    }
    setIsPrinting(false);
    setPrintOrderId(null);
  }, [printOrderId]);

  // No store selected state
  if (!clientId) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <div className="mb-4">
              <i className="bi bi-shop display-1 text-muted"></i>
            </div>
            <h2 className="mb-3">Select a Store First</h2>
            <p className="text-muted mb-4">
              Please select a store to view your order history.
            </p>
            <Link to="/account" className="btn btn-primary btn-lg">
              Select Store
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Page Header */}
      <div className="row mb-4">
        <div className="col">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h1 className="mb-1">Order History</h1>
              <p className="text-muted mb-0">
                View and track orders for {currentStore?.name || "your store"}
              </p>
            </div>
            <Link to="/categories" className="btn btn-primary">
              <i className="bi bi-plus-lg me-2"></i>
              New Order
            </Link>
          </div>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="row mb-4">
        <div className="col">
          <ul className="nav nav-tabs">
            {Object.entries(ORDER_STATUS_TABS).map(([key, config]) => (
              <li className="nav-item" key={key}>
                <button
                  className={`nav-link ${activeTab === key ? "active" : ""}`}
                  onClick={() => handleTabChange(key)}
                >
                  {config.label}
                  {activeTab === key && totalCount > 0 && (
                    <span className="badge bg-primary ms-2">{totalCount}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="alert alert-danger mb-4">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Failed to load orders. Please try again.
          <button className="btn btn-link p-0 ms-2" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      )}

      {/* Search Bar - always visible when we have data or are searching */}
      {(totalCount > 0 || searchTerm || debouncedSearchTerm) && (
        <div className="row mb-3">
          <div className="col-md-4">
            <div className="input-group">
              <span className="input-group-text bg-white">
                <i className="bi bi-search text-muted"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search by order #..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
              {searchTerm && (
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => { setSearchTerm(""); setDebouncedSearchTerm(""); setPage(1); }}
                >
                  <i className="bi bi-x"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content Area */}
      {isLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading orders...</p>
        </div>
      ) : orders.length === 0 && !debouncedSearchTerm && !searchTerm ? (
        /* Empty State - no orders and no search */
        <div className="text-center py-5">
          <i className={`bi ${ORDER_STATUS_TABS[activeTab].emptyIcon} display-1 text-muted mb-3 d-block`}></i>
          <h4 className="text-muted mb-3">{ORDER_STATUS_TABS[activeTab].emptyMessage}</h4>
          <p className="text-muted mb-4">
            {activeTab === "pendingDelivery"
              ? "You don't have any pending deliveries right now."
              : "Start shopping to place your first order!"}
          </p>
          <Link to="/categories" className="btn btn-primary">
            Browse Products
          </Link>
        </div>
      ) : orders.length === 0 && debouncedSearchTerm ? (
        /* No Search Results */
        <div className="text-center py-5">
          <i className="bi bi-search display-1 text-muted mb-3 d-block"></i>
          <h4 className="text-muted mb-3">No orders match "{debouncedSearchTerm}"</h4>
          <p className="text-muted mb-4">
            Try a different order number or clear the search.
          </p>
        </div>
      ) : (
        /* Orders Table */
        <>
          <div className="card border-0 shadow-sm">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "40px" }}></th>
                    <th>Order #</th>
                    <th
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('datePlaced')}
                    >
                      Date Placed
                      <i className={`bi ms-1 ${
                        sortConfig.column === 'datePlaced'
                          ? sortConfig.direction === 'asc'
                            ? 'bi-sort-up'
                            : 'bi-sort-down'
                          : 'bi-arrow-down-up text-muted'
                      }`}></i>
                    </th>
                    <th
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('deliveryDate')}
                    >
                      Delivery Date
                      <i className={`bi ms-1 ${
                        sortConfig.column === 'deliveryDate'
                          ? sortConfig.direction === 'asc'
                            ? 'bi-sort-up'
                            : 'bi-sort-down'
                          : 'bi-arrow-down-up text-muted'
                      }`}></i>
                    </th>
                    <th className="text-center">Items</th>
                    <th className="text-end">Total</th>
                    <th className="text-center">Payment Status</th>
                    <th className="text-center">Delivery Status</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <OrderRow
                      key={order.orderID}
                      order={order}
                      isExpanded={expandedOrder === order.orderID}
                      onToggle={() => toggleOrderExpand(order.orderID)}
                      orderDetails={expandedOrder === order.orderID ? orderDetails : null}
                      isLoadingDetails={expandedOrder === order.orderID && isLoadingDetails}
                      onPrint={handlePrintOrder}
                      isPrinting={isPrinting}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-4">
              <p className="text-muted mb-0">
                Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount} orders
              </p>
              <nav aria-label="Orders pagination">
                <ul className="pagination mb-0">
                  <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <i className="bi bi-chevron-left"></i>
                    </button>
                  </li>
                  {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = idx + 1;
                    } else if (page <= 3) {
                      pageNum = idx + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + idx;
                    } else {
                      pageNum = page - 2 + idx;
                    }
                    return (
                      <li key={pageNum} className={`page-item ${page === pageNum ? "active" : ""}`}>
                        <button
                          className="page-link"
                          onClick={() => setPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      </li>
                    );
                  })}
                  <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <i className="bi bi-chevron-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </>
      )}

      {/* Hidden OrderPDF for printing */}
      {printOrderId && (
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
          <OrderPDF ref={printRef} orderId={printOrderId} onReady={handlePrintReady} />
        </div>
      )}
    </div>
  );
};

export default OrderHistoryPage;
